import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tv, Loader2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      toast.success("Welcome back");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      const msg = formatApiError(err);
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-navy">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-12 bg-navy text-white relative overflow-hidden">
        <div
          className="absolute inset-0 opacity-[0.06]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 20% 30%, #F59E0B 0%, transparent 40%), radial-gradient(circle at 80% 70%, #DC2626 0%, transparent 40%)",
          }}
        />
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-md bg-white/5 border border-white/10 flex items-center justify-center">
            <Tv className="w-6 h-6 text-gold" />
          </div>
          <div>
            <div className="font-heading text-2xl font-semibold">Top Media Corp.</div>
            <div className="text-xs tracking-[0.25em] uppercase text-white/60">Entertainment Company</div>
          </div>
        </div>
        <div className="relative z-10 space-y-4">
          <h1 className="font-heading text-5xl font-bold leading-tight">
            Sales, stock & <span className="text-gold">subscriptions</span> — in one calm dashboard.
          </h1>
          <p className="text-white/70 max-w-md">
            Internal CRM for managing customers, daily sales, inventory and reports. Lightweight, fast,
            built for the shop floor.
          </p>
        </div>
        <div className="relative z-10 text-xs text-white/40">© {new Date().getFullYear()} Top Media Corp.</div>
      </div>

      {/* Right login form */}
      <div className="flex items-center justify-center p-6 bg-white">
        <Card className="w-full max-w-md p-8 border-border/70 shadow-sm">
          <div className="lg:hidden flex items-center gap-3 mb-6">
            <Tv className="w-6 h-6 text-navy" />
            <span className="font-heading text-xl font-semibold text-navy">Top Media Corp.</span>
          </div>
          <h2 className="font-heading text-2xl font-semibold text-navy">Sign in</h2>
          <p className="text-sm text-muted-foreground mt-1">Use your staff or admin credentials.</p>

          <form onSubmit={onSubmit} className="mt-6 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                data-testid="login-email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@topmediacorp.ca"
                autoComplete="email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                required
                data-testid="login-password-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
              />
            </div>
            {error && <div className="text-sm text-destructive" data-testid="login-error">{error}</div>}
            <Button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full bg-navy hover:bg-navy-hover text-white"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
